import React, { useCallback, useEffect, useId, useRef } from "react"
import Quill from "quill"
import "quill/dist/quill.snow.css"
import VariableInsertButton from "@/components/shared/VariableInsertButton"
import { formatPlainTextAsHtmlEmail, looksLikeHtmlEmail } from "@/lib/textTemplateVariables"
import { normalizeQuillHtmlForEmail } from "@/lib/emailHtmlNormalize"
import "./EmailRichTextEditor.css"

const EMPTY_HTML = "<p><br></p>"

const DEFAULT_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["clean"],
]

export function normalizeEmailEditorValue(value) {
  if (!value || !String(value).trim()) {
    return ""
  }

  const text = String(value)
  if (looksLikeHtmlEmail(text)) {
    return text
  }

  return formatPlainTextAsHtmlEmail(text)
}

export function normalizeEmailEditorOutput(html) {
  if (!html || html === EMPTY_HTML) {
    return ""
  }

  return html
}

/**
 * Simple rich-text email body editor (Quill). Stores lightweight HTML
 * (paragraphs, bold, lists). Plain-text templates are upgraded on load.
 */
export default function EmailRichTextEditor({
  value = "",
  onChange,
  onBlur,
  disabled = false,
  placeholder = "Write your email...",
  variables = null,
  legacy = false,
  buttonVariant = "outline",
  minHeight = 180,
  className = "",
  id: idProp,
  label,
  toolbarActions = null,
}) {
  const wrapperRef = useRef(null)
  const quillRef = useRef(null)
  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  const isInternalChangeRef = useRef(false)
  const reactId = useId()
  const hostId = idProp || `email-rich-text-${reactId.replace(/:/g, "")}`

  onChangeRef.current = onChange
  onBlurRef.current = onBlur

  // Initialize Quill once; cleanup removes toolbar + editor from wrapper.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || quillRef.current) {
      return undefined
    }

    const mount = document.createElement("div")
    mount.className = "email-rich-text-editor-mount"
    wrapper.appendChild(mount)

    const quill = new Quill(mount, {
      theme: "snow",
      placeholder,
      modules: {
        toolbar: DEFAULT_TOOLBAR,
        clipboard: { matchVisual: false },
      },
    })

    quillRef.current = quill

    const initial = normalizeEmailEditorValue(value)
    if (initial) {
      quill.clipboard.dangerouslyPasteHTML(initial)
    }

    quill.on("text-change", () => {
      isInternalChangeRef.current = true
      const rawHtml = normalizeEmailEditorOutput(quill.root.innerHTML)
      const html = normalizeQuillHtmlForEmail(rawHtml)
      onChangeRef.current?.(html)
      queueMicrotask(() => {
        isInternalChangeRef.current = false
      })
    })

    quill.on("selection-change", (range, oldRange) => {
      if (range === null && oldRange !== null) {
        onBlurRef.current?.()
      }
    })

    return () => {
      quillRef.current = null
      wrapper.innerHTML = ""
    }
  }, [])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill) {
      return
    }
    quill.root.dataset.placeholder = placeholder
  }, [placeholder])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill || isInternalChangeRef.current) {
      return
    }

    const normalized = normalizeEmailEditorValue(value)
    const current = normalizeEmailEditorOutput(quill.root.innerHTML)
    if (normalized === current) {
      return
    }

    const selection = quill.getSelection()
    if (normalized) {
      quill.clipboard.dangerouslyPasteHTML(normalized)
    } else {
      quill.setText("")
    }

    if (selection) {
      quill.setSelection(selection)
    }
  }, [value])

  useEffect(() => {
    quillRef.current?.enable(!disabled)
  }, [disabled])

  const insertText = useCallback(
    (text) => {
      const quill = quillRef.current
      if (!quill || disabled) {
        return
      }

      const range = quill.getSelection(true)
      const index = range ? range.index : Math.max(0, quill.getLength() - 1)
      quill.insertText(index, text, "user")
      quill.setSelection(index + text.length)
      quill.focus()
    },
    [disabled],
  )

  const showHeader = Boolean(label || toolbarActions || (variables && variables.length > 0))

  return (
    <div
      className={`email-rich-text-editor ${className} ${disabled ? "is-readonly" : ""}`}
    >
      {showHeader && (
        <div className="email-rich-text-editor-header">
          {label ? <span className="email-rich-text-editor-label">{label}</span> : <span />}
          <div className="email-rich-text-editor-actions">
            {toolbarActions}
            {variables && variables.length > 0 && (
              <VariableInsertButton
                onInsert={insertText}
                variables={variables}
                legacy={legacy}
                disabled={disabled}
                variant={buttonVariant}
              />
            )}
          </div>
        </div>
      )}
      <div
        id={hostId}
        ref={wrapperRef}
        className="email-rich-text-editor-quill-host"
        style={{ "--email-editor-min-height": `${minHeight}px` }}
      />
    </div>
  )
}
